import { useEffect, useMemo, useState } from "react";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Eyebrow } from "../components/Eyebrow";
import { Icon } from "../components/Icon";
import { Skeleton } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { supabase } from "../lib/supabase";
import { fmtK, fmtKShort } from "../lib/money";
import { resolveImage } from "../lib/images";
import { parseTs } from "../lib/dateUtils";
import { RANGES, EARNED, VOIDED, startOfRange, niceMax, growthPct } from "./seller-analytics/constants";
import { RevenueChart } from "./seller-analytics/RevenueChart";
import { DeliveryDonut } from "./seller-analytics/DeliveryDonut";
import { TopProductsChart } from "./seller-analytics/TopProductsChart";
import { RatingBars } from "./seller-analytics/RatingBars";
import { KpiBox } from "./seller-analytics/KpiBox";
import { MetricRow } from "./seller-analytics/MetricRow";

export function SellerAnalytics({ seller }) {
  const [rangeId, setRangeId] = useState("30d");
  const range = RANGES.find((r) => r.id === rangeId) || RANGES[2];
  const days = range.days;

  const [subOrders, setSubOrders] = useState([]);
  const [prevSubOrders, setPrevSubOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!seller?.id) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      const sinceDate = startOfRange(days);
      const since = sinceDate ? sinceDate.toISOString() : null;

      // Previous window of equal length (for growth indicators).
      let prevStart = null;
      let prevEnd = null;
      if (sinceDate && days != null) {
        prevEnd = new Date(sinceDate);
        prevStart = new Date(sinceDate);
        prevStart.setDate(prevStart.getDate() - days);
      }

      const soQ = supabase
        .from("sub_orders")
        .select(
          "id, total, status, created_at, updated_at, delivery_method, order_items(id, product_id, quantity, total, unit_price, products(title, images))"
        )
        .eq("seller_id", seller.id)
        .order("created_at", { ascending: false })
        .limit(2000);
      if (since) soQ.gte("created_at", since);

      const prevQ = prevStart
        ? supabase
            .from("sub_orders")
            .select("id, total, status, created_at")
            .eq("seller_id", seller.id)
            .gte("created_at", prevStart.toISOString())
            .lt("created_at", prevEnd.toISOString())
            .limit(2000)
        : Promise.resolve({ data: [] });

      const prodQ = supabase
        .from("products")
        .select("id, title, view_count, total_sold, stock_qty, price, images, created_at, status")
        .eq("seller_id", seller.id)
        .is("deleted_at", null)
        .limit(200);

      const revQ = supabase.from("reviews").select("rating, created_at").eq("seller_id", seller.id);
      if (since) revQ.gte("created_at", since);

      const [soRes, prevRes, prodRes, revRes] = await Promise.all([soQ, prevQ, prodQ, revQ]);
      if (cancelled) return;
      setSubOrders(soRes.data || []);
      setPrevSubOrders(prevRes.data || []);
      setProducts(prodRes.data || []);
      setReviews(revRes.data || []);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [seller?.id, days]);

  // ============= Series bucketing =============
  // Choose granularity based on window size so bars are readable.
  const { series, xLabel } = useMemo(() => {
    if (!subOrders || subOrders.length === 0 || days === 0) return emptySeries(days);
    const mode = days === 1 ? "hourly" : days == null || days > 90 ? "monthly" : days > 30 ? "weekly" : "daily";

    const bins = new Map();
    const now = new Date();

    if (mode === "hourly") {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      for (let h = 0; h <= 23; h++) {
        const k = h;
        bins.set(k, { key: k, label: `${h}h`, revenue: 0, orders: 0 });
      }
      subOrders.forEach((o) => {
        const d = parseTs(o.created_at);
        if (!d || d < start) return;
        const h = d.getHours();
        const bin = bins.get(h);
        if (!bin) return;
        bin.orders += 1;
        if (EARNED.has(o.status)) bin.revenue += o.total || 0;
      });
      return { series: [...bins.values()], xLabel: "Hour" };
    }

    if (mode === "daily") {
      for (let i = days - 1; i >= 0; i--) {
        const d = new Date(now);
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() - i);
        const k = dayKey(d);
        bins.set(k, {
          key: k,
          label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          revenue: 0,
          orders: 0,
        });
      }
      subOrders.forEach((o) => {
        const d = parseTs(o.created_at);
        if (!d) return;
        const k = dayKey(d);
        const bin = bins.get(k);
        if (!bin) return;
        bin.orders += 1;
        if (EARNED.has(o.status)) bin.revenue += o.total || 0;
      });
      return { series: [...bins.values()], xLabel: "Day" };
    }

    if (mode === "weekly") {
      // Last ceil(days/7) weeks, anchored to end of today.
      const weeks = Math.ceil(days / 7);
      for (let i = weeks - 1; i >= 0; i--) {
        const end = new Date(now);
        end.setHours(0, 0, 0, 0);
        end.setDate(end.getDate() - i * 7);
        const start = new Date(end);
        start.setDate(end.getDate() - 6);
        const k = dayKey(start);
        bins.set(k, {
          key: k,
          start,
          end,
          label: `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
          revenue: 0,
          orders: 0,
        });
      }
      const keys = [...bins.values()].sort((a, b) => a.start - b.start);
      subOrders.forEach((o) => {
        const d = parseTs(o.created_at);
        if (!d) return;
        const bin = keys.find((b) => d >= b.start && d <= b.end);
        if (!bin) return;
        bin.orders += 1;
        if (EARNED.has(o.status)) bin.revenue += o.total || 0;
      });
      return { series: keys, xLabel: "Week of" };
    }

    // monthly — all-time or > 90d
    const monthBins = new Map();
    // Cover last 12 months if days=null, else from earliest order.
    let anchor = new Date();
    anchor.setDate(1);
    anchor.setHours(0, 0, 0, 0);
    const earliest = subOrders.reduce((min, o) => {
      const d = parseTs(o.created_at);
      if (!d) return min;
      return !min || d < min ? d : min;
    }, null);
    const startMonth = earliest
      ? new Date(earliest.getFullYear(), earliest.getMonth(), 1)
      : new Date(anchor.getFullYear(), anchor.getMonth() - 11, 1);
    for (let m = new Date(startMonth); m <= anchor; m.setMonth(m.getMonth() + 1)) {
      const k = `${m.getFullYear()}-${m.getMonth()}`;
      monthBins.set(k, {
        key: k,
        label: m.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
        revenue: 0,
        orders: 0,
      });
    }
    subOrders.forEach((o) => {
      const d = parseTs(o.created_at);
      if (!d) return;
      const k = `${d.getFullYear()}-${d.getMonth()}`;
      const bin = monthBins.get(k);
      if (!bin) return;
      bin.orders += 1;
      if (EARNED.has(o.status)) bin.revenue += o.total || 0;
    });
    return { series: [...monthBins.values()], xLabel: "Month" };
  }, [subOrders, days]);

  // ============= KPIs =============
  const earned = useMemo(() => subOrders.filter((o) => EARNED.has(o.status)), [subOrders]);
  const prevEarned = useMemo(() => prevSubOrders.filter((o) => EARNED.has(o.status)), [prevSubOrders]);

  const revenue = earned.reduce((s, o) => s + (o.total || 0), 0);
  const prevRevenue = prevEarned.reduce((s, o) => s + (o.total || 0), 0);
  const orderCount = earned.length;
  const prevOrderCount = prevEarned.length;
  const aov = orderCount > 0 ? revenue / orderCount : 0;
  const prevAov = prevOrderCount > 0 ? prevRevenue / prevOrderCount : 0;
  const units = earned.reduce(
    (s, o) => s + (o.order_items || []).reduce((a, it) => a + (it.quantity || 0), 0),
    0
  );
  const totalViews = products.reduce((s, p) => s + (p.view_count || 0), 0);

  // ============= Top products =============
  const topProducts = useMemo(() => {
    const tally = new Map();
    earned.forEach((o) => {
      (o.order_items || []).forEach((it) => {
        if (!it.product_id) return;
        const entry = tally.get(it.product_id) || {
          id: it.product_id,
          title: it.products?.title || "Product",
          images: it.products?.images,
          units: 0,
          revenue: 0,
        };
        entry.units += it.quantity || 0;
        entry.revenue += it.total || (it.unit_price || 0) * (it.quantity || 0);
        tally.set(it.product_id, entry);
      });
    });
    return [...tally.values()].sort((a, b) => b.revenue - a.revenue).slice(0, 5);
  }, [earned]);

  // ============= Delivery mix =============
  const deliveryMix = useMemo(() => {
    const mix = {};
    earned.forEach((o) => {
      const k = o.delivery_method || "unknown";
      mix[k] = (mix[k] || 0) + 1;
    });
    const total = Object.values(mix).reduce((s, v) => s + v, 0) || 1;
    return Object.entries(mix)
      .map(([k, v]) => ({ method: k, count: v, pct: (v / total) * 100 }))
      .sort((a, b) => b.count - a.count);
  }, [earned]);

  // ============= Rating distribution =============
  const ratingDist = useMemo(() => {
    const dist = [0, 0, 0, 0, 0];
    reviews.forEach((r) => {
      const i = Math.max(0, Math.min(4, (r.rating || 0) - 1));
      dist[i] += 1;
    });
    return dist;
  }, [reviews]);
  const ratingTotal = ratingDist.reduce((s, v) => s + v, 0);
  const ratingAvg =
    ratingTotal > 0 ? ratingDist.reduce((s, v, i) => s + v * (i + 1), 0) / ratingTotal : 0;

  // ============= Business health metrics =============
  // Conversion rate = orders / views
  const conversionPct = totalViews > 0 ? (orderCount / totalViews) * 100 : 0;

  // Cart abandonment proxy: payment_failed / (payment_failed + confirmed/...)
  const paymentFailures = subOrders.filter(
    (o) => o.status === "payment_failed" || o.status === "payment_timeout"
  ).length;
  const paymentAttempts = subOrders.length;
  const abandonPct = paymentAttempts > 0 ? (paymentFailures / paymentAttempts) * 100 : 0;

  // Cancellation rate = cancelled / (cancelled + earned)
  const cancelledCount = subOrders.filter((o) => VOIDED.has(o.status)).length;
  const cancelBase = cancelledCount + orderCount;
  const cancelPct = cancelBase > 0 ? (cancelledCount / cancelBase) * 100 : 0;

  // Fulfillment lag: avg hours from confirmed → shipped (proxy with created → updated for shipped+ states).
  const shippedOrders = subOrders.filter((o) =>
    ["shipped", "delivered", "completed"].includes(o.status)
  );
  const fulfillLagHours =
    shippedOrders.length > 0
      ? shippedOrders.reduce((s, o) => {
          const c = parseTs(o.created_at);
          const u = parseTs(o.updated_at);
          if (!c || !u) return s;
          return s + (u - c) / 3_600_000;
        }, 0) / shippedOrders.length
      : 0;

  // Delivery time: avg days from order → delivered (for delivered/completed).
  const deliveredOrders = subOrders.filter((o) => EARNED.has(o.status));
  const deliveryDays =
    deliveredOrders.length > 0
      ? deliveredOrders.reduce((s, o) => {
          const c = parseTs(o.created_at);
          const u = parseTs(o.updated_at);
          if (!c || !u) return s;
          return s + (u - c) / 86_400_000;
        }, 0) / deliveredOrders.length
      : 0;

  // Inventory turnover proxy = units sold / (stock_qty + units)
  const totalStock = products.reduce((s, p) => s + (p.stock_qty || 0), 0);
  const turnoverPct = totalStock + units > 0 ? (units / (totalStock + units)) * 100 : 0;

  // Repeat buyer proxy: unique buyers w/ >1 earned order.
  // (sub_orders select doesn't include buyer_id — compute best-effort via orders lookup would cost another query.
  // Use order_items groupings as a loose proxy: orders per buyer not available here, so skip repeat and use a placeholder.)

  if (!seller) {
    return <EmptyState icon="store" title="No seller account" body="Analytics require a seller profile." />;
  }

  return (
    <div>
      {/* Header + range filter */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 18,
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <Eyebrow>Analytics</Eyebrow>
          <h1
            style={{
              fontSize: 26,
              fontWeight: 900,
              color: C.charcoal,
              margin: 0,
              letterSpacing: "-0.5px",
            }}
          >
            {range.label}
          </h1>
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4 }}>
            {loading ? "Loading…" : `${orderCount} earned orders · ${units} units · ${reviews.length} reviews`}
          </div>
        </div>
        <div
          style={{
            display: "inline-flex",
            padding: 3,
            borderRadius: 12,
            background: C.cream,
            gap: 2,
          }}
        >
          {RANGES.map((r) => {
            const on = rangeId === r.id;
            return (
              <button
                key={r.id}
                onClick={() => setRangeId(r.id)}
                style={{
                  border: "none",
                  background: on ? C.white : "transparent",
                  color: on ? C.hunter : C.muted,
                  fontSize: 11.5,
                  fontWeight: 800,
                  padding: "6px 12px",
                  borderRadius: 9,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ display: "grid", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={90} />
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
            <Skeleton height={280} />
            <Skeleton height={280} />
          </div>
          <Skeleton height={200} />
        </div>
      ) : (
        <>
          {/* ============= KPI row ============= */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 14,
              marginBottom: 18,
            }}
          >
            <KpiBox
              label="Revenue"
              value={fmtK(revenue)}
              growth={days != null ? growthPct(revenue, prevRevenue) : null}
              note="vs. previous period"
            />
            <KpiBox
              label="Orders"
              value={String(orderCount)}
              growth={days != null ? growthPct(orderCount, prevOrderCount) : null}
              note="earned (delivered/completed)"
            />
            <KpiBox
              label="AOV"
              value={fmtK(aov)}
              growth={days != null ? growthPct(aov, prevAov) : null}
              note="avg order value"
            />
            <KpiBox label="Views" value={totalViews.toLocaleString()} note="all products" />
          </div>

          {/* ============= Revenue chart + Delivery donut ============= */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: 14,
              marginBottom: 18,
            }}
          >
            <Card pad={22}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div>
                  <Eyebrow>Revenue over time</Eyebrow>
                  <div style={{ fontSize: 20, fontWeight: 900, color: C.charcoal, letterSpacing: "-0.4px" }}>
                    {fmtK(revenue)}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
                  {xLabel}
                </div>
              </div>
              {series.length === 0 || series.every((d) => d.revenue === 0) ? (
                <div style={{ fontSize: 13, color: C.muted, padding: "40px 0", textAlign: "center" }}>
                  No earned revenue in this window yet.
                </div>
              ) : (
                <RevenueChart series={series} />
              )}
            </Card>

            <Card pad={22}>
              <Eyebrow>Delivery mix</Eyebrow>
              <div style={{ fontSize: 20, fontWeight: 900, color: C.charcoal, letterSpacing: "-0.4px", marginBottom: 14 }}>
                {orderCount} orders
              </div>
              {deliveryMix.length === 0 ? (
                <div style={{ fontSize: 13, color: C.muted, padding: "40px 0", textAlign: "center" }}>
                  No earned orders yet.
                </div>
              ) : (
                <DeliveryDonut mix={deliveryMix} />
              )}
            </Card>
          </div>

          {/* ============= Top products + Rating dist ============= */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: 14,
              marginBottom: 18,
            }}
          >
            <Card pad={22}>
              <Eyebrow>Top products</Eyebrow>
              <div style={{ fontSize: 20, fontWeight: 900, color: C.charcoal, letterSpacing: "-0.4px", marginBottom: 14 }}>
                By revenue
              </div>
              {topProducts.length === 0 ? (
                <div style={{ fontSize: 13, color: C.muted, padding: "30px 0", textAlign: "center" }}>
                  Nothing sold in this window.
                </div>
              ) : (
                <TopProductsChart products={topProducts} />
              )}
            </Card>

            <Card pad={22}>
              <Eyebrow>Ratings</Eyebrow>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 14 }}>
                <div style={{ fontSize: 32, fontWeight: 900, color: C.charcoal, letterSpacing: "-1px" }}>
                  {ratingAvg.toFixed(1)}
                </div>
                <div style={{ fontSize: 12, color: C.muted, fontWeight: 700 }}>
                  {ratingTotal} review{ratingTotal === 1 ? "" : "s"}
                </div>
              </div>
              {ratingTotal === 0 ? (
                <div style={{ fontSize: 13, color: C.muted, padding: "20px 0", textAlign: "center" }}>
                  No reviews yet.
                </div>
              ) : (
                <RatingBars dist={ratingDist} total={ratingTotal} />
              )}
            </Card>
          </div>

          {/* ============= Business health ============= */}
          <div style={{ marginBottom: 14 }}>
            <Eyebrow>Business health</Eyebrow>
            <h2 style={{ fontSize: 18, fontWeight: 900, color: C.charcoal, margin: "4px 0 14px" }}>
              How your store is doing
            </h2>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <MetricRow
              icon="target"
              title="Conversion rate"
              value={`${conversionPct.toFixed(2)}%`}
              status={benchStatus(conversionPct, [1, 2.5])}
              recommendation={
                conversionPct < 1
                  ? "Conversion is low. Improve product photos, prices, and descriptions. Consider running a promotion."
                  : conversionPct < 2.5
                  ? "Conversion is average. A/B test titles and add urgency (low-stock badges, promos)."
                  : "Strong conversion — your listings are working."
              }
              benchmarks={[
                { label: "Weak", value: "< 1%" },
                { label: "Average", value: "1–2.5%" },
                { label: "Strong", value: "> 2.5%" },
              ]}
            />
            <MetricRow
              icon="shopping-bag"
              title="Average order value"
              value={fmtK(aov)}
              status={aov >= 15000 ? "great" : aov >= 5000 ? "good" : "warn"}
              recommendation={
                aov < 5000
                  ? "AOV is low. Try bundling related items or offering free delivery over a threshold."
                  : aov < 15000
                  ? "AOV is decent. Cross-sell on the product page to lift basket size."
                  : "Excellent AOV — buyers trust you with larger tickets."
              }
              benchmarks={[
                { label: "Low", value: "< K50" },
                { label: "Mid", value: "K50–150" },
                { label: "High", value: "> K150" },
              ]}
            />
            <MetricRow
              icon="refresh-cw"
              title="Inventory turnover"
              value={`${turnoverPct.toFixed(1)}%`}
              status={benchStatus(turnoverPct, [10, 30])}
              recommendation={
                turnoverPct < 10
                  ? "Stock is moving slowly. Discount aging items or reduce restock sizes."
                  : turnoverPct < 30
                  ? "Turnover is steady. Keep an eye on dead stock."
                  : "Great turnover — you're selling through quickly."
              }
              benchmarks={[
                { label: "Slow", value: "< 10%" },
                { label: "Steady", value: "10–30%" },
                { label: "Hot", value: "> 30%" },
              ]}
            />
            <MetricRow
              icon="x-circle"
              title="Cart abandonment"
              value={`${abandonPct.toFixed(1)}%`}
              status={benchStatus(abandonPct, [10, 30], true)}
              recommendation={
                abandonPct > 30
                  ? "Many checkouts fail. Verify your mobile money number is active and Kazang/DPO is configured."
                  : abandonPct > 10
                  ? "Some abandonment is normal. Ensure prices + delivery options are clear on the product page."
                  : "Checkout is clean — buyers are finishing the purchase."
              }
              benchmarks={[
                { label: "Great", value: "< 10%" },
                { label: "OK", value: "10–30%" },
                { label: "High", value: "> 30%" },
              ]}
            />
            <MetricRow
              icon="alert-circle"
              title="Cancellation rate"
              value={`${cancelPct.toFixed(1)}%`}
              status={benchStatus(cancelPct, [5, 15], true)}
              recommendation={
                cancelPct > 15
                  ? "Cancellations are hurting trust. Ship faster, double-check stock, and update buyers proactively."
                  : cancelPct > 5
                  ? "A few cancellations is normal. Keep communication open with buyers."
                  : "Very low cancels — buyers are getting what they ordered."
              }
              benchmarks={[
                { label: "Great", value: "< 5%" },
                { label: "OK", value: "5–15%" },
                { label: "High", value: "> 15%" },
              ]}
            />
            <MetricRow
              icon="clock"
              title="Fulfillment lag"
              value={`${fulfillLagHours.toFixed(1)} hrs`}
              status={benchStatus(fulfillLagHours, [24, 72], true)}
              recommendation={
                fulfillLagHours > 72
                  ? "You're slow to dispatch. Ship the same day when possible — fast shipping boosts ratings."
                  : fulfillLagHours > 24
                  ? "You're dispatching in a day. Aim for under 24h to stand out."
                  : "Fast dispatch — buyers will notice."
              }
              benchmarks={[
                { label: "Fast", value: "< 24h" },
                { label: "OK", value: "24–72h" },
                { label: "Slow", value: "> 72h" },
              ]}
            />
            <MetricRow
              icon="truck"
              title="Average delivery time"
              value={`${deliveryDays.toFixed(1)} days`}
              status={benchStatus(deliveryDays, [2, 5], true)}
              recommendation={
                deliveryDays > 5
                  ? "Delivery is slow. Consider pickup or a courier with better tracking."
                  : deliveryDays > 2
                  ? "Delivery time is reasonable. Communicate ETAs clearly."
                  : "Delivery is quick — this is a competitive advantage."
              }
              benchmarks={[
                { label: "Fast", value: "< 2d" },
                { label: "OK", value: "2–5d" },
                { label: "Slow", value: "> 5d" },
              ]}
            />
            <MetricRow
              icon="star"
              title="Review rating"
              value={`${ratingAvg.toFixed(2)} / 5`}
              status={ratingAvg >= 4.5 ? "great" : ratingAvg >= 3.5 ? "good" : ratingTotal === 0 ? "good" : "warn"}
              recommendation={
                ratingTotal === 0
                  ? "No reviews yet. Politely ask buyers to rate after delivery."
                  : ratingAvg < 3.5
                  ? "Ratings are low. Read feedback carefully and respond — many buyers will update a review."
                  : ratingAvg < 4.5
                  ? "Solid ratings. Keep responding to reviews to build trust."
                  : "Top-tier ratings — keep doing what you're doing."
              }
              benchmarks={[
                { label: "Weak", value: "< 3.5" },
                { label: "Solid", value: "3.5–4.5" },
                { label: "Top", value: "> 4.5" },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ============= Helpers =============

function emptySeries(days) {
  const mode = days === 1 ? "Hour" : days == null || days > 90 ? "Month" : days > 30 ? "Week of" : "Day";
  return { series: [], xLabel: mode };
}

function dayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function benchStatus(value, thresholds, inverted = false) {
  const [low, high] = thresholds;
  if (inverted) {
    if (value > high) return "warn";
    if (value > low) return "good";
    return "great";
  }
  if (value < low) return "warn";
  if (value < high) return "good";
  return "great";
}

// ============= Charts =============
